<template>
  <v-container fluid>
    <v-row class="mb-2" align="center">
      <v-col cols="12" md="7">
        <h1 class="text-h5 font-weight-bold">Panel de plataforma</h1>
        <p class="grey--text mb-0">
          Estado comercial y operativo de la nube iTPV.
          <span v-if="overview"> Actualizado {{ formatDate(overview.generatedAt, true) }}.</span>
        </p>
      </v-col>
      <v-col cols="12" md="5" class="d-flex justify-md-end align-center">
        <v-select
          v-model="expiryWindowDays"
          :items="expiryWindowOptions"
          label="Ventana de vencimiento"
          dense
          outlined
          hide-details
          style="max-width: 220px"
          class="mr-2"
          @change="loadOverview"
        />
        <v-btn icon :loading="loading" title="Actualizar" @click="refresh">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" text dense class="mb-4">{{ error }}</v-alert>

    <v-alert
      v-if="attentionMessages.length"
      type="warning"
      text
      border="left"
      class="mb-4"
    >
      <div class="font-weight-medium mb-1">Requiere atención</div>
      <ul class="mb-0 pl-4">
        <li v-for="message in attentionMessages" :key="message">{{ message }}</li>
      </ul>
    </v-alert>

    <!-- Indicadores comerciales -->
    <v-row v-if="loading">
      <v-col v-for="i in 6" :key="`kpi-${i}`" cols="12" sm="6" md="4" lg="2">
        <v-skeleton-loader type="list-item-avatar-two-line" />
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col v-for="card in kpiCards" :key="card.key" cols="12" sm="6" md="4" lg="2">
        <PlatformKpiCard v-bind="card" />
      </v-col>
    </v-row>

    <!-- Salud del sistema (resumen) -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold py-3">
            <v-icon left small>mdi-heart-pulse</v-icon>
            Estado del sistema
            <HealthStatusChip v-if="health" :status="health.overall" class="ml-3" />
            <v-spacer />
            <v-btn small text color="primary" to="/platform/system">
              Ver detalle
              <v-icon right small>mdi-chevron-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />

          <v-card-text v-if="healthLoading">
            <v-skeleton-loader type="list-item-three-line" />
          </v-card-text>

          <v-card-text v-else-if="healthError" class="red--text">
            {{ healthError }}
          </v-card-text>

          <v-card-text v-else-if="health">
            <v-row>
              <v-col cols="12" md="4">
                <div class="d-flex align-center mb-2">
                  <v-icon small left>mdi-server</v-icon>
                  <span class="text-subtitle-2">API</span>
                  <v-spacer />
                  <HealthStatusChip :status="health.api.status" />
                </div>
                <div class="text-caption grey--text text--darken-1">
                  Activa desde hace {{ formatUptime(health.api.uptimeSeconds) }} ·
                  Node {{ health.api.nodeVersion }} · {{ health.api.environment }}
                </div>
                <div class="text-caption grey--text text--darken-1">
                  Memoria {{ formatSize(health.api.memory.rssMb) }} de
                  {{ formatSize(health.api.memory.systemTotalMb) }} del servidor
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <div class="d-flex align-center mb-2">
                  <v-icon small left>mdi-database</v-icon>
                  <span class="text-subtitle-2">Base central</span>
                  <v-spacer />
                  <HealthStatusChip :status="health.centralDatabase.status" />
                </div>
                <div class="text-caption grey--text text--darken-1">
                  {{ health.centralDatabase.name || '—' }} ·
                  respuesta {{ health.centralDatabase.latencyMs }} ms
                </div>
                <div class="text-caption grey--text text--darken-1">
                  {{ health.tenantPool.cachedConnections }} conexiones tenant en caché
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <div class="d-flex align-center mb-2">
                  <v-icon small left>mdi-database-cog</v-icon>
                  <span class="text-subtitle-2">MySQL</span>
                  <v-spacer />
                  <HealthStatusChip :status="health.mysql.status" />
                </div>

                <template v-if="health.mysql.maxConnections">
                  <div class="d-flex justify-space-between text-caption grey--text text--darken-1">
                    <span>
                      {{ health.mysql.threadsConnected }} / {{ health.mysql.maxConnections }} conexiones
                    </span>
                    <span>{{ health.mysql.connectionUsagePercent }}%</span>
                  </div>
                  <v-progress-linear
                    :value="health.mysql.connectionUsagePercent"
                    :color="connectionUsageColor"
                    height="6"
                    rounded
                    class="my-1"
                  />
                  <div class="text-caption grey--text text--darken-1">
                    {{ health.mysql.threadsRunning }} en ejecución ·
                    activo desde hace {{ formatUptime(health.mysql.uptimeSeconds) }}
                  </div>
                </template>
                <div v-else class="text-caption grey--text text--darken-1">
                  {{ health.mysql.message || 'Métricas del servidor no disponibles.' }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Vencimientos -->
    <v-row class="mt-2">
      <v-col cols="12" lg="7">
        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold py-3">
            <v-icon left small>mdi-calendar-clock</v-icon>
            Suscripciones por vencer
            <v-chip v-if="expiringList.length" x-small class="ml-2" color="grey lighten-3">
              {{ expiringList.length }}
            </v-chip>
          </v-card-title>
          <v-divider />

          <v-skeleton-loader v-if="loading" type="table-tbody" />

          <v-data-table
            v-else
            :headers="expiringHeaders"
            :items="expiringList"
            :items-per-page="8"
            :footer-props="{ 'items-per-page-options': [8, 15, 50] }"
            dense
            no-data-text="Ninguna suscripción vence en esta ventana."
            @click:row="goToBusiness"
          >
            <template v-slot:[`item.businessName`]="{ item }">
              <div class="font-weight-medium">{{ item.businessName }}</div>
              <div class="text-caption grey--text">{{ item.dbName }}</div>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <SubscriptionStatusChip :status="item.status" />
            </template>

            <template v-slot:[`item.currentPeriodEnd`]="{ item }">
              {{ formatDate(item.currentPeriodEnd) }}
            </template>

            <template v-slot:[`item.daysRemaining`]="{ item }">
              <v-chip
                x-small
                label
                :color="`${expirySeverity(item.daysRemaining).color} lighten-4`"
                :text-color="`${expirySeverity(item.daysRemaining).color} darken-4`"
              >
                {{ expirySeverity(item.daysRemaining).label }}
              </v-chip>
            </template>

            <template v-slot:[`item.userCount`]="{ item }">
              {{ item.userCount }}
            </template>
          </v-data-table>

          <v-card-text v-if="subscriptions.expiringListTruncated" class="text-caption grey--text pt-0">
            Se muestran los 50 vencimientos más próximos.
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="5">
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-bold py-3">
            <v-icon left small>mdi-chart-donut</v-icon>
            Suscripciones por estado
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-skeleton-loader v-if="loading" type="image" />
            <ApexChart
              v-else-if="statusChart.series.length"
              type="donut"
              height="260"
              :options="statusChart.options"
              :series="statusChart.series"
            />
            <div v-else class="text-center grey--text py-6">Sin suscripciones registradas.</div>
          </v-card-text>
        </v-card>

        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold py-3">
            <v-icon left small>mdi-trending-up</v-icon>
            Altas de negocios por mes
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-skeleton-loader v-if="loading" type="image" />
            <ApexChart
              v-else-if="signupsChart.series[0].data.length"
              type="bar"
              height="220"
              :options="signupsChart.options"
              :series="signupsChart.series"
            />
            <div v-else class="text-center grey--text py-6">Sin altas en los últimos 12 meses.</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Uso por negocio e incidencias -->
    <v-row class="mt-2">
      <v-col cols="12" lg="7">
        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold py-3">
            <v-icon left small>mdi-account-group</v-icon>
            Negocios con más usuarios
            <v-spacer />
            <v-btn small text color="primary" to="/platform/businesses">Ver todos</v-btn>
          </v-card-title>
          <v-divider />

          <v-skeleton-loader v-if="loading" type="table-tbody" />

          <v-data-table
            v-else
            :headers="usageHeaders"
            :items="topBusinesses"
            :items-per-page="10"
            hide-default-footer
            dense
            no-data-text="Aún no hay negocios con cuentas vinculadas."
            @click:row="goToBusiness"
          >
            <template v-slot:[`item.businessName`]="{ item }">
              <div class="font-weight-medium">{{ item.businessName }}</div>
              <div class="text-caption grey--text">{{ item.dbName }}</div>
            </template>

            <template v-slot:[`item.businessStatus`]="{ item }">
              <BusinessStatusChip :status="item.businessStatus" />
            </template>

            <template v-slot:[`item.userCount`]="{ item }">
              {{ item.activeUserCount }} / {{ item.userCount }}
            </template>

            <template v-slot:[`item.posDevices`]="{ item }">
              <span :class="{ 'grey--text': !item.posDevices }">{{ item.posDevices }}</span>
            </template>

            <template v-slot:[`item.lastPosActivityAt`]="{ item }">
              {{ formatDate(item.lastPosActivityAt, true) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12" lg="5">
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-bold py-3">
            <v-icon left small>mdi-account-key</v-icon>
            Cuentas de la plataforma
          </v-card-title>
          <v-divider />
          <v-list dense>
            <v-list-item v-for="row in accountRows" :key="row.label">
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ row.label }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <span class="font-weight-bold" :class="row.color">{{ row.value }}</span>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>

        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold py-3">
            <v-icon left small :color="provisioningIssues.length ? 'red' : 'grey'">
              mdi-alert-octagon-outline
            </v-icon>
            Incidencias de aprovisionamiento
          </v-card-title>
          <v-divider />

          <v-card-text v-if="!provisioningIssues.length" class="grey--text text-body-2">
            Sin fallos registrados.
          </v-card-text>

          <v-list v-else dense two-line>
            <v-list-item
              v-for="issue in provisioningIssues"
              :key="issue.id"
              :to="`/platform/businesses/${issue.businessId}`"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ issue.businessName || `Negocio #${issue.businessId}` }} — {{ issue.action }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ formatDate(issue.createdAt, true) }}
                  <span v-if="issue.details && issue.details.error"> · {{ issue.details.error }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <v-card outlined>
          <v-card-text class="d-flex flex-wrap align-center">
            <v-btn color="primary" to="/platform/businesses/new" class="mr-2 mb-2">
              <v-icon left>mdi-plus</v-icon>
              Nuevo negocio
            </v-btn>
            <v-btn outlined to="/platform/businesses" class="mr-2 mb-2">
              <v-icon left>mdi-domain</v-icon>
              Negocios
            </v-btn>
            <v-btn outlined to="/platform/system" class="mb-2">
              <v-icon left>mdi-server-network</v-icon>
              Sistema
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import BusinessStatusChip from '@/components/platform/BusinessStatusChip.vue';
import SubscriptionStatusChip from '@/components/platform/SubscriptionStatusChip.vue';
import PlatformKpiCard from '@/components/platform/PlatformKpiCard.vue';
import HealthStatusChip from '@/components/platform/HealthStatusChip.vue';
import { fetchPlatformOverview, fetchPlatformHealth } from '@/services/platform/platformDashboardService';
import {
  expirySeverity,
  formatUptime,
  formatSize,
  formatDate,
  formatCurrency,
  formatMonth,
} from '@/utils/platformFormatters';

const SUBSCRIPTION_STATUS_LABELS = {
  active: 'Activas',
  trialing: 'En prueba',
  past_due: 'Vencidas',
  canceled: 'Canceladas',
  expired: 'Expiradas',
};

const STATUS_COLORS = {
  active: '#2ECA71',
  trialing: '#29B6F6',
  past_due: '#FFA726',
  canceled: '#9E9E9E',
  expired: '#EF5350',
};

/** La salud del sistema se refresca sola; el resumen comercial no lo necesita. */
const HEALTH_REFRESH_MS = 60000;

export default {
  name: 'PlatformDashboardView',
  components: {
    BusinessStatusChip,
    SubscriptionStatusChip,
    PlatformKpiCard,
    HealthStatusChip,
  },
  data() {
    return {
      loading: true,
      healthLoading: true,
      error: '',
      healthError: '',
      overview: null,
      health: null,
      expiryWindowDays: 30,
      expiryWindowOptions: [
        { text: 'Próximos 7 días', value: 7 },
        { text: 'Próximos 15 días', value: 15 },
        { text: 'Próximos 30 días', value: 30 },
        { text: 'Próximos 60 días', value: 60 },
        { text: 'Próximos 90 días', value: 90 },
      ],
      expiringHeaders: [
        { text: 'Negocio', value: 'businessName' },
        { text: 'Estado', value: 'status', width: 110 },
        { text: 'Vence', value: 'currentPeriodEnd', width: 130 },
        { text: 'Restan', value: 'daysRemaining', width: 130 },
        { text: 'Usuarios', value: 'userCount', align: 'end', width: 90 },
      ],
      usageHeaders: [
        { text: 'Negocio', value: 'businessName' },
        { text: 'Estado', value: 'businessStatus', width: 130 },
        { text: 'Usuarios activos', value: 'userCount', align: 'end', width: 130 },
        { text: 'Cajas POS', value: 'posDevices', align: 'end', width: 100 },
        { text: 'Última actividad POS', value: 'lastPosActivityAt', width: 170 },
      ],
      healthTimer: null,
    };
  },
  computed: {
    businesses() {
      return this.overview?.businesses || {};
    },
    subscriptions() {
      return this.overview?.subscriptions || {};
    },
    accounts() {
      return this.overview?.accounts || {};
    },
    posDevices() {
      return this.overview?.posDevices || {};
    },
    expiringList() {
      return this.subscriptions.expiringList || [];
    },
    topBusinesses() {
      return this.overview?.topBusinessesByUsers || [];
    },
    provisioningIssues() {
      return this.overview?.provisioningIssues || [];
    },
    primaryRevenue() {
      const revenue = this.subscriptions.revenue || [];
      return revenue.length ? revenue[0] : null;
    },
    kpiCards() {
      const expiringSoon = this.subscriptions.expiringIn30 || 0;
      const lapsed = this.subscriptions.expired || 0;

      return [
        {
          key: 'businesses',
          label: 'Negocios activos',
          value: `${this.businesses.active || 0} / ${this.businesses.total || 0}`,
          hint: `${this.businesses.createdLast30Days || 0} altas en 30 días`,
          icon: 'mdi-domain',
          color: 'indigo',
          to: '/platform/businesses',
        },
        {
          key: 'subscriptions',
          label: 'Suscripciones vigentes',
          value: this.subscriptions.effectiveActive || 0,
          hint: `${this.subscriptions.withoutSubscription || 0} negocios sin suscripción`,
          icon: 'mdi-card-account-details-outline',
          color: 'teal',
        },
        {
          key: 'expiring',
          label: 'Vencen en 30 días',
          value: expiringSoon,
          hint: `${this.subscriptions.expiringIn7 || 0} en los próximos 7 días`,
          icon: 'mdi-calendar-alert',
          color: expiringSoon ? 'orange' : 'grey',
          alert: (this.subscriptions.expiringIn7 || 0) > 0,
        },
        {
          key: 'lapsed',
          label: 'Vencidas sin renovar',
          value: lapsed,
          hint: 'Activas con periodo terminado',
          icon: 'mdi-calendar-remove',
          color: lapsed ? 'red' : 'grey',
          alert: lapsed > 0,
        },
        {
          key: 'users',
          label: 'Usuarios en negocios',
          value: this.accounts.linkedToBusiness || 0,
          hint: `${this.posDevices.active || 0} cajas POS con sesión activa`,
          icon: 'mdi-account-group',
          color: 'blue',
        },
        {
          key: 'revenue',
          label: 'Ingreso recurrente',
          value: this.primaryRevenue
            ? formatCurrency(this.primaryRevenue.monthly, this.primaryRevenue.currency)
            : '—',
          hint: this.primaryRevenue
            ? `${formatCurrency(this.primaryRevenue.annualized, this.primaryRevenue.currency)} anualizado`
            : 'Sin suscripciones vigentes',
          icon: 'mdi-cash-multiple',
          color: 'green',
        },
      ];
    },
    accountRows() {
      return [
        { label: 'Cuentas totales', value: this.accounts.total || 0, color: '' },
        { label: 'Activas', value: this.accounts.active || 0, color: 'green--text' },
        { label: 'Desactivadas', value: this.accounts.inactive || 0, color: this.accounts.inactive ? 'orange--text' : '' },
        {
          label: 'Invitaciones sin aceptar',
          value: this.accounts.pendingInvitations || 0,
          color: this.accounts.pendingInvitations ? 'orange--text' : '',
        },
        { label: 'Super administradores', value: this.accounts.superAdmins || 0, color: '' },
        { label: 'Cajas POS activas (7 días)', value: this.posDevices.activeLast7Days || 0, color: '' },
      ];
    },
    attentionMessages() {
      if (this.loading) return [];
      const messages = [];

      if (this.subscriptions.expired) {
        messages.push(`${this.subscriptions.expired} suscripción(es) siguen marcadas como activas pero su periodo ya terminó.`);
      }
      if (this.subscriptions.expiringIn7) {
        messages.push(`${this.subscriptions.expiringIn7} suscripción(es) vencen en los próximos 7 días.`);
      }
      if (this.businesses.provisioningFailed) {
        messages.push(`${this.businesses.provisioningFailed} negocio(s) quedaron con aprovisionamiento fallido.`);
      }
      if (this.subscriptions.withoutSubscription) {
        messages.push(`${this.subscriptions.withoutSubscription} negocio(s) no tienen suscripción registrada.`);
      }
      if (this.health && this.health.tenantDatabases.missingDatabases) {
        messages.push(`${this.health.tenantDatabases.missingDatabases} negocio(s) apuntan a una base de datos que no existe en MySQL.`);
      }

      return messages;
    },
    connectionUsageColor() {
      const usage = this.health?.mysql?.connectionUsagePercent || 0;
      if (usage >= 90) return 'red';
      if (usage >= 70) return 'orange';
      return 'green';
    },
    statusChart() {
      const byStatus = this.subscriptions.byStatus || {};
      const entries = Object.entries(byStatus).filter(([, count]) => count > 0);

      return {
        series: entries.map(([, count]) => count),
        options: {
          chart: { toolbar: { show: false } },
          labels: entries.map(([status]) => SUBSCRIPTION_STATUS_LABELS[status] || status),
          colors: entries.map(([status]) => STATUS_COLORS[status] || '#90A4AE'),
          legend: { position: 'bottom' },
          dataLabels: { enabled: true },
          plotOptions: {
            pie: {
              donut: {
                labels: {
                  show: true,
                  total: { show: true, label: 'Total', color: '#616161' },
                },
              },
            },
          },
        },
      };
    },
    signupsChart() {
      const signups = this.businesses.monthlySignups || [];

      return {
        series: [{ name: 'Altas', data: signups.map((row) => row.count) }],
        options: {
          chart: { toolbar: { show: false } },
          colors: ['#0066CC'],
          plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
          dataLabels: { enabled: false },
          grid: { borderColor: '#eeeeee' },
          xaxis: { categories: signups.map((row) => formatMonth(row.month)) },
          yaxis: { labels: { formatter: (value) => Math.round(value) } },
        },
      };
    },
  },
  created() {
    this.loadOverview();
    this.loadHealth();
    this.healthTimer = setInterval(() => this.loadHealth(), HEALTH_REFRESH_MS);
  },
  beforeDestroy() {
    if (this.healthTimer) clearInterval(this.healthTimer);
  },
  methods: {
    expirySeverity,
    formatUptime,
    formatSize,
    formatDate,
    formatCurrency,
    async loadOverview() {
      this.loading = true;
      this.error = '';

      try {
        this.overview = await fetchPlatformOverview({ expiryWindowDays: this.expiryWindowDays });
      } catch (error) {
        this.error = error.response?.data?.mensaje || 'No se pudo cargar el resumen de la plataforma.';
      } finally {
        this.loading = false;
      }
    },
    async loadHealth() {
      this.healthError = '';

      try {
        this.health = await fetchPlatformHealth();
      } catch (error) {
        this.healthError = error.response?.data?.mensaje || 'No se pudo consultar el estado del sistema.';
      } finally {
        this.healthLoading = false;
      }
    },
    refresh() {
      this.loadOverview();
      this.loadHealth();
    },
    goToBusiness(item) {
      this.$router.push(`/platform/businesses/${item.businessId}`);
    },
  },
};
</script>

<style scoped>
::v-deep .v-data-table tbody tr {
  cursor: pointer;
}
</style>
